import { useEffect, useState } from 'react';
import axios from 'axios';
import LightAdmin from 'components/headers/lightAdmin';
import AnimationRevealPage from 'helpers/AnimationRevealPage';
import Footer from "components/footers/Footer.jsx";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Swal from "sweetalert2";
import { Alert } from 'react-bootstrap';
import { FaEdit, FaTrash } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import ReactQuill from "react-quill";

const ProduitsAdmin = () => {
    const { i18n, t } = useTranslation();

    // --- Quill Configuration ---
    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean'],
        ],
    };

    const quillFormats = [
        'header', 'bold', 'italic', 'underline', 'strike',
        'align', 'list', 'bullet', 'link',
    ];

    // Fixed renderHTML to handle objects vs strings
    const renderHTML = (content) => {
        const htmlString = typeof content === 'string' ? content : '';
        return { __html: htmlString || '' };
    };

    // --- State ---
    const [searchTerm, setSearchTerm] = useState("");
    const [products, setProducts] = useState([]);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [editingProductId, setEditingProductId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("All");

    const [newProduct, setNewProduct] = useState({
        Name: { fr: '', ar: '' },
        slug: { fr: '', ar: '' },
        Description: { fr: '', ar: '' },
        short_description: { fr: '', ar: '' },
        user_price: '',
        distributeur_price: '',
        old_price: '',
        StockQuantity: '',
        Category: 'Cosmetique',
        image: '',
        additional_images: []
    });

    // --- Helpers ---
    const parseIfJson = (str) => {
        if (typeof str !== 'string') return str;
        const trimmed = str.trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try { return JSON.parse(trimmed); } catch { return str; }
        }
        return str;
    };

    const ensureLanguageObject = (data) => {
        const parsed = parseIfJson(data);
        if (parsed && typeof parsed === 'object' && (parsed.fr !== undefined || parsed.ar !== undefined)) {
            return { fr: parsed.fr || '', ar: parsed.ar || '' };
        }
        return { fr: parsed || '', ar: '' };
    };

    // --- Logic ---
    useEffect(() => {
        checkAdminStatus();
    }, [i18n.language]);

    const checkAdminStatus = () => {
        const adminStatus = localStorage.getItem('isAdmin') === '1';
        setIsAdmin(adminStatus);
        if (adminStatus) fetchAllProducts();
    };

    const fetchAllProducts = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;

            const response = await axios.get(`https://rehabwmg.online/api/admin/product?lang=${i18n.language}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200) {
                const parsed = response.data.map(product => ({
                    ...product,
                    Name: parseIfJson(product.Name),
                    slug: parseIfJson(product.slug),
                    Description: parseIfJson(product.Description),
                    short_description: parseIfJson(product.short_description),
                }));
                setProducts(parsed);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };

    const handleEdit = (productId) => {
        const product = products.find(p => p.ProductID === productId);
        if (!product) return;

        setEditingProductId(productId);
        setNewProduct({
            Name: ensureLanguageObject(product.Name),
            slug: ensureLanguageObject(product.slug),
            Description: ensureLanguageObject(product.Description),
            short_description: ensureLanguageObject(product.short_description),
            user_price: product.user_price || '',
            distributeur_price: product.distributeur_price || '',
            old_price: product.old_price || '',
            Category: product.Category || 'Cosmetique',
            StockQuantity: product.StockQuantity || '',
            image: product.image || '',
            additional_images: []
        });
        setShowModal(true);
    };

    const handleDelete = async (productId) => {
        const result = await Swal.fire({
            title: 'Êtes-vous sûr?',
            text: "Vous ne pourrez pas revenir en arrière!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Oui, supprimer!',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('authToken');
                await axios.delete(`https://rehabwmg.online/api/admin/products/${productId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setProducts(products.filter(p => p.ProductID !== productId));
                Swal.fire('Supprimé!', 'Le produit a été supprimé.', 'success');
            } catch (error) {
                console.error(error);
            }
        }
    };

    const handleShowModal = () => {
        setEditingProductId(null);
        setNewProduct({
            Name: { fr: '', ar: '' },
            slug: { fr: '', ar: '' },
            Description: { fr: '', ar: '' },
            short_description: { fr: '', ar: '' },
            user_price: '',
            distributeur_price: '',
            old_price: '',
            Category: 'Alimentaire',
            StockQuantity: '',
            image: '',
            additional_images: []
        });
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleChange = (e) => {
        setNewProduct({ ...newProduct, [e.target.name]: e.target.value });
    };

    const handleMultipleImagesChange = (e) => {
        const files = Array.from(e.target.files);
        setNewProduct(prev => ({
            ...prev,
            additional_images: [...prev.additional_images, ...files]
        }));
    };

    const removeAdditionalImage = (index) => {
        setNewProduct(prev => ({
            ...prev,
            additional_images: prev.additional_images.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const formData = new FormData();
            formData.append('Name', JSON.stringify(newProduct.Name));
            formData.append('slug', JSON.stringify(newProduct.slug));
            formData.append('Description', JSON.stringify(newProduct.Description));
            formData.append('short_description', JSON.stringify(newProduct.short_description));
            formData.append('user_price', newProduct.user_price);
            formData.append('distributeur_price', newProduct.distributeur_price);
            formData.append('StockQuantity', newProduct.StockQuantity);
            formData.append('Category', newProduct.Category);
            formData.append('old_price', newProduct.old_price);

            if (newProduct.image instanceof File) {
                formData.append('image', newProduct.image);
            }
            newProduct.additional_images.forEach((file, index) => {
                formData.append(`additional_images[${index}]`, file);
            });

            const url = editingProductId 
                ? `https://rehabwmg.online/api/admin/products/${editingProductId}?_method=PUT`
                : 'https://rehabwmg.online/api/admin/products';

            const response = await axios.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200 || response.status === 201) {
                fetchAllProducts();
                handleCloseModal();
                Swal.fire('Succès!', `Le produit a été ${editingProductId ? 'modifié' : 'ajouté'}.`, 'success');
            }
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    const filteredProducts = products
        .filter(product => {
            const name = i18n.language === "ar" ? (product.Name?.ar || product.Name?.fr || '') : (product.Name?.fr || product.Name?.ar || '');
            return name.toLowerCase().includes(searchTerm.toLowerCase());
        })
        .filter(product => selectedCategory === "All" ? true : product.Category === selectedCategory);

    if (!isAdmin) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <Alert variant="danger" style={{ width: '80%', textAlign: 'center' }}>Unauthorized: You do not have admin access.</Alert>
            </div>
        );
    }
console.log(products);
    return (
        <AnimationRevealPage>
            <LightAdmin />
            <div className="d-flex flex-column">
                <section className="w-100 p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="text-dark display-4">{t('Tableau_de_bord_Produits')}</h6>
                        <div className="d-flex align-items-center">
                            <button className="btn btn-lg" style={{ backgroundColor: '#4e7662', color: 'white' }} onClick={handleShowModal}>
                                {t('ajouter_un_produit')}
                            </button>
                            <input
                                type="text"
                                className="form-control w-auto ms-3"
                                placeholder={t('Rechercher_par_nom_produits')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <hr className="my-4" />

                    <div className="mb-3 d-flex justify-content-center flex-wrap gap-2">
                        {["All", "Packs", "Alimentaire", "Cosmetique", "Garence", "Shampoing"].map(cat => (
                            <button
                                key={cat}
                                className="btn"
                                onClick={() => setSelectedCategory(cat)}
                                style={{
                                    backgroundColor: selectedCategory === cat ? '#3b5c4d' : '#4e7662',
                                    color: 'white', border: 'none', padding: '10px 20px', borderRadius: '5px'
                                }}
                            >
                                {t(cat)}
                            </button>
                        ))}
                    </div>

                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead style={{ backgroundColor: '#AFA4CE' }}>
                                <tr>
                                    <th>ID</th><th>Ref</th><th>Nom</th><th>Slug</th>
                                    <th>Courte description</th><th>Description</th>
                                    <th>Stock</th><th>Prix User</th><th>Prix Dist.</th>
                                    <th>Catégorie</th><th>Image</th><th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map((product) => {
                                    const currentName = i18n.language === "ar" ? (product.Name?.ar || product.Name?.fr || '') : (product.Name?.fr || product.Name?.ar || '');
                                    const currentSlug = i18n.language === "ar" ? (product.slug?.ar || product.slug?.fr || '') : (product.slug?.fr || product.slug?.ar || '');
                                    const currentShort = i18n.language === "ar" ? (product.short_description?.ar || product.short_description?.fr || '') : (product.short_description?.fr || product.short_description?.ar || '');
                                    const currentDesc = i18n.language === "ar" ? (product.Description?.ar || product.Description?.fr || '') : (product.Description?.fr || product.Description?.ar || '');

                                    return (
                                        <tr key={product.ProductID}>
                                            <td>{product.ProductID}</td>
                                            <td>{product.Ref}</td>
                                            <td>{currentName}</td>
                                            <td>{currentSlug}</td>
                                            <td>
                                                <div style={{ display: "-webkit-box", WebkitLineClamp: expandedRows[`${product.ProductID}-short`] ? "unset" : 2, WebkitBoxOrient: "vertical", overflow: "hidden" }} 
                                                     dangerouslySetInnerHTML={renderHTML(currentShort)} />
                                                {currentShort.length > 50 && (
                                                    <button className="btn btn-link btn-sm p-0" onClick={() => setExpandedRows(prev => ({ ...prev, [`${product.ProductID}-short`]: !prev[`${product.ProductID}-short`] }))}>
                                                        {expandedRows[`${product.ProductID}-short`] ? t("Voir moins") : t("Voir plus")}
                                                    </button>
                                                )}
                                            </td>
                                            <td>
                                                <div style={{ display: "-webkit-box", WebkitLineClamp: expandedRows[product.ProductID] ? "unset" : 3, WebkitBoxOrient: "vertical", overflow: "hidden" }} 
                                                     dangerouslySetInnerHTML={renderHTML(currentDesc)} />
                                                {currentDesc.length > 50 && (
                                                    <button className="btn btn-link btn-sm p-0" onClick={() => setExpandedRows(prev => ({ ...prev, [product.ProductID]: !prev[product.ProductID] }))}>
                                                        {expandedRows[product.ProductID] ? t("Voir moins") : t("Voir plus")}
                                                    </button>
                                                )}
                                            </td>
                                            <td>{product.StockQuantity}</td>
                                            <td>{product.user_price}{t("currency")}</td>
                                            <td>{product.distributeur_price} {t("currency")}</td>
                                            <td>{product.Category}</td>
                                            <td>
                                                {product.image ? <img src={`https://rehabwmg.online/storage/${product.image}`} alt="img" style={{ width: '50px' }} /> : 'No Image'}
                                            </td>
                                            <td>
                                                <button className="btn btn-warning btn-sm me-2" onClick={() => handleEdit(product.ProductID)}><FaEdit /></button>
                                                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(product.ProductID)}><FaTrash /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
            <Footer />

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingProductId ? t('Modifier_le_produit') : t('ajouter_un_produit')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <div className="row">
                            <Form.Group className="col-md-6">
                                <Form.Label>{t('Nom')} (FR)</Form.Label>
                                <Form.Control type="text" value={newProduct.Name.fr} onChange={(e) => setNewProduct(prev => ({ ...prev, Name: { ...prev.Name, fr: e.target.value } }))} required />
                            </Form.Group>
                            <Form.Group className="col-md-6">
                                <Form.Label>{t('Nom')} (AR)</Form.Label>
                                <Form.Control type="text" dir="rtl" value={newProduct.Name.ar} onChange={(e) => setNewProduct(prev => ({ ...prev, Name: { ...prev.Name, ar: e.target.value } }))} required />
                            </Form.Group>
                        </div>

                        <Form.Group className="mt-3">
                            <Form.Label>{t('short_description')} (FR)</Form.Label>
                            <ReactQuill theme="snow" value={newProduct.short_description.fr} onChange={(val) => setNewProduct(prev => ({ ...prev, short_description: { ...prev.short_description, fr: val } }))} modules={quillModules} formats={quillFormats} />
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label>{t('short_description')} (AR)</Form.Label>
                            <ReactQuill theme="snow" value={newProduct.short_description.ar} onChange={(val) => setNewProduct(prev => ({ ...prev, short_description: { ...prev.short_description, ar: val } }))} modules={quillModules} formats={quillFormats} />
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label>{t('Description')} (FR)</Form.Label>
                            <ReactQuill theme="snow" value={newProduct.Description.fr} onChange={(val) => setNewProduct(prev => ({ ...prev, Description: { ...prev.Description, fr: val } }))} modules={quillModules} formats={quillFormats} />
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label>{t('Description')} (AR)</Form.Label>
                            <ReactQuill theme="snow" value={newProduct.Description.ar} onChange={(val) => setNewProduct(prev => ({ ...prev, Description: { ...prev.Description, ar: val } }))} modules={quillModules} formats={quillFormats} />
                        </Form.Group>

                        <div className="row mt-3">
                            <Form.Group className="col-md-4">
                                <Form.Label>{t('Prix_utilisateur')}</Form.Label>
                                <Form.Control type="number" name="user_price" value={newProduct.user_price} onChange={handleChange} required />
                            </Form.Group>
                            <Form.Group className="col-md-4">
                                <Form.Label>{t('Prix_distributeur')}</Form.Label>
                                <Form.Control type="number" name="distributeur_price" value={newProduct.distributeur_price} onChange={handleChange} required />
                            </Form.Group>
                            <Form.Group className="col-md-4">
                                <Form.Label>{t('Ancienne_prix')}</Form.Label>
                                <Form.Control type="number" name="old_price" value={newProduct.old_price} onChange={handleChange} />
                            </Form.Group>
                        </div>

                        <div className="row mt-3">
                            <Form.Group className="col-md-6">
                                <Form.Label>{t('Quantité_en_stock')}</Form.Label>
                                <Form.Control type="number" name="StockQuantity" value={newProduct.StockQuantity} onChange={handleChange} required />
                            </Form.Group>
                            <Form.Group className="col-md-6">
                                <Form.Label>{t('Catégorie')}</Form.Label>
                                <Form.Control as="select" name="Category" value={newProduct.Category} onChange={handleChange}>
                                    <option value="Complément">{t('Complément')}</option>
                                    <option value="Cosmetique">{t('Cosmetique')}</option>
                                    <option value="Packs">{t('Packs')}</option>
                                    <option value="Shampoing">{t('Shampoing')}</option>
                                    <option value="Garence">{t('Garence')}</option>
                                </Form.Control>
                            </Form.Group>
                        </div>

                        <Form.Group className="mt-3">
                            <Form.Label>{t('Image Principale')}</Form.Label>
                            <Form.Control type="file" onChange={(e) => setNewProduct({ ...newProduct, image: e.target.files[0] })} />
                        </Form.Group>

                        <Form.Group className="mt-3">
                            <Form.Label>{t('Images Supplémentaires')}</Form.Label>
                            <Form.Control type="file" multiple onChange={handleMultipleImagesChange} />
                        </Form.Group>

                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {newProduct.additional_images?.map((file, index) => (
                                <div key={index} className="position-relative">
                                    <img src={URL.createObjectURL(file)} alt="preview" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '5px' }} />
                                    <button type="button" onClick={() => removeAdditionalImage(index)} className="btn btn-danger btn-sm position-absolute top-0 end-0" style={{ padding: '0 5px' }}>x</button>
                                </div>
                            ))}
                        </div>

                        <Button variant="primary" type="submit" className="mt-4 w-100" disabled={loading}>
                            {loading ? t('Chargement...') : editingProductId ? t('Modifier') : t('Ajouter')}
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </AnimationRevealPage>
    );
};

export default ProduitsAdmin;